<template>
  <div class="media-hotspot relative text-white overflow-hidden h-full w-full border border-gray-800 bg-gray-900 rounded-lg">
    <header class="bg-transparent absolute top-0 left-0 w-full z-20 p-6 text-center">
      <div class="flex items-center justify-center">
        <h3 class="text-2xl font-normal tracking-wide text-shadow" v-text="hotspot.title" />
        <button class="p-1 ml-2" @click="toggleImageDescription">
          <InformationCircleIcon v-if="hotspot.content" class="h-6 w-6" />
        </button>
      </div>
    </header>
    <p
      v-if="hotspot.content"
      class="image-description pointer-events-none absolute top-20 left-0 w-full h-full max-h-full p-4 m-0 bg-black bg-opacity-60 transition-all whitespace-pre-line z-30"
      :class="isDescriptionOpenLocally ? 'translate-y-none overflow-auto opacity-100' : 'translate-y-2/4 opacity-0'"
    >
      <button class="block mx-auto p-1" @click="toggleImageDescription">
        <ChevronUpIcon
          class="icon w-6 m-auto transition-transform block mb-4 pointer-events-auto"
          :class="{ 'rotate-180': isDescriptionOpenLocally }"
        />
      </button>
      {{ hotspot.content }}
    </p>
    <img
      v-if="type === 'I'"
      ref="image"
      class="z-10 h-full w-full absolute inset-0 object-contain"
      :src="hotspot.media[0].url || undefined"
      :height="dimensions.height"
      :width="dimensions.width"
      :style="`aspect-ratio: ${dimensions.width / dimensions.height}`"
    />
    <iframe
      v-if="type === 'V'"
      class="aspect-video max-w-full w-full max-h-full z-10"
      :src="hotspot.media[0].url || undefined"
      frameborder="0"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      height="1920"
      width="1080"
    />
  </div>
</template>

<script lang="ts" setup>
import { ChevronUpIcon } from '@heroicons/vue/20/solid'
import type { Hotspot, HotspotType } from '@/entities/site'
import { InformationCircleIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  hotspot: {
    type: Object as PropType<Hotspot>,
    required: true,
  },
  type: {
    type: String as PropType<HotspotType>,
    required: true,
  },
  contentStyle: {
    type: Object as PropType<Partial<CSSStyleDeclaration>>,
    default: () => ({}),
  },
  isDescriptionOpen: {
    type: Boolean,
    default: false,
  },
  allowExpansion: {
    type: Boolean,
    default: () => true,
  },
  dimensions: {
    type: Object,
    default: () => ({
      width: 100,
      height: 100,
    }),
  },
})

const emit = defineEmits(['update:image-dimensions', 'toggle-image-description'])

const { isDescriptionOpen } = toRefs(props)
const image = ref<HTMLImageElement | undefined>()
const isDescriptionOpenLocally = ref(false)

watch(
  isDescriptionOpen,
  isDescriptionOpen => {
    isDescriptionOpenLocally.value = isDescriptionOpen
  },
  { immediate: true },
)

function toggleImageDescription() {
  if (!props.allowExpansion) return
  isDescriptionOpenLocally.value = !isDescriptionOpenLocally.value
  emit('toggle-image-description')
}
</script>
